import { OrganizationService } from 'src/app/core/service/organization.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { PatientImportValidators } from 'src/app/core/validators/patient-import.validators';
import { PatientImportEdit } from '../../model/patient-import';
import { Observable } from 'rxjs';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { EmailTemplateService } from 'src/app/core/service/email-template.service';
import { SettingService } from 'src/app/core/service/setting.service';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { ChannelService } from 'src/app/core/service/channel.service';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { sortBy } from 'lodash';
import { Organization } from 'src/app/core/model/organization';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { User } from 'src/app/user/model/user';
import { UserService } from 'src/app/core/service/user.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';

@Component({
  standalone: false,
  selector: 'app-patient-import-edit',
  templateUrl: './patient-import-edit.component.html',
  styleUrls: ['./patient-import-edit.component.scss']
})
export class PatientImportEditComponent implements OnInit
{
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<PatientImportEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public sendInviteTemplates = [];
  public communityEnabled = false;
  public isLocked = false;
  public importFileSelected = false;
  public allChannels = [];
  public groupedChannels: GroupResult[] = [];
  public allChannelsReferrals = [];
  public groupedChannelsReferrals: GroupResult[] = [];
  public patientLabel = {};

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * List of available organizations
   */
  public organizations: Organization[];

  /**
   * Filtered list of organizations for drop-down.
   */
  public organizationsFiltered: Organization[] = [];

  public systemSubjectIdentifiers: Array<{ displayName: string; id: string }>;

  /**
   * Current logged in user
   */
  private currentUser: User;
  private currentUserOrganization: Organization;
  private selectedFile: any = null;
  private selectedFileMimeType: string;
  private defaultValidDays = 30;
  private defaultSendInviteTemplate = null;
  private patientImportEdit: PatientImportEdit;
  private defaultBatchText: string;

  constructor(
    private translate: TranslateService,
    private settingService: SettingService,
    private fb: UntypedFormBuilder,
    private emailTemplateService: EmailTemplateService,
    private messageHandlerService: MessageHandlerService,
    private channelService: ChannelService,
    private organizationService: OrganizationService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private patientImportValidators: PatientImportValidators,
    private dictionaryService: DictionaryService)
  { }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get automaticImport(): UntypedFormControl
  {
    return this.editForm.get('automaticImport');
  }

  public get automaticDiscardOnError(): UntypedFormControl
  {
    return this.editForm.get('automaticDiscardOnError');
  }

  public get sendInvite(): UntypedFormControl
  {
    return this.editForm.get('sendInvite');
  }

  public get inviteTemplate(): UntypedFormControl
  {
    return this.editForm.get('inviteTemplate');
  }

  public get inviteSendOn(): UntypedFormControl
  {
    return this.editForm.get('inviteSendOn');
  }

  public get inviteValidDays(): UntypedFormControl
  {
    return this.editForm.get('inviteValidDays');
  }

  public get importFilename(): UntypedFormControl
  {
    return this.editForm.get('importFilename');
  }

  public get channel(): UntypedFormControl
  {
    return this.editForm.get('channel');
  }

  public get referralChannel(): UntypedFormControl
  {
    return this.editForm.get('referralChannel');
  }

  public get organization(): UntypedFormControl
  {
    return this.editForm.get('organization');
  }

  public get contactCreationLevel(): UntypedFormControl
  {
    return this.editForm.get('contactCreationLevel');
  }

  public get pcpCreationLevel(): UntypedFormControl
  {
    return this.editForm.get('pcpCreationLevel');
  }

  public get subjectIdentifiers(): UntypedFormControl
  {
    return this.editForm.get('subjectIdentifiers');
  }

  @Input() public set model(patientImportBatch: PatientImportEdit)
  {
    this.initModel(patientImportBatch);
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
        this.defaultValidDays = sessionSetting.sendInviteDefaultValidDays;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };

        this.emailTemplateService.getCachedEmailTemplatesList('CommunityInvite').subscribe({
          next: (data) =>
          {
            this.sendInviteTemplates = data;
            if (sessionSetting.sendInviteDefaultEmailTemplate !== null && sessionSetting.sendInviteDefaultEmailTemplate !== '')
            {
              const templateObject = this.sendInviteTemplates.find(x =>
                x.id === parseInt(sessionSetting.sendInviteDefaultEmailTemplate, 10)
              );
              this.defaultSendInviteTemplate = templateObject !== undefined ? templateObject : null;
            }
          },
          error: errorResponse =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      }
    });

    // Get Organizations
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
        this.filterOrganizations('');
      }
    });

    this.dictionaryService.getCachedIdentifiers().subscribe({
      next: (data) =>
      {
        this.systemSubjectIdentifiers = sortBy(data.map(d => ({ id: d.id.toString(), displayName: d.displayName })), ['displayName']);
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;

    // transform subject identifiers array to string
    if (objectToPersist.subjectIdentifiers instanceof Array)
    {
      objectToPersist.subjectIdentifiers = objectToPersist.subjectIdentifiers.join();
    }

    // Validate subject identifier(s) for the selected organization
    this.settingService.getSettingByKey('SubjectIdentifiers', objectToPersist.organization.id).subscribe({
      next: (setting: any) =>
      {
        if (setting.value === '' && this.subjectIdentifiers.value === null)
        {
          this.messageHandlerService.displayWarning('ERROR-SUBJECT-IDENTIFIERS-NOT-DEFINED');
        }
        else
        {
          if (objectToPersist.sendInvite === undefined || objectToPersist.sendInvite === null || !objectToPersist.sendInvite)
          {
            objectToPersist.sendInvite = false;
            objectToPersist.inviteTemplate = null;
            objectToPersist.inviteSendOn = null;
            objectToPersist.inviteValidDays = null;
          }

          if (this.isNew)
          {
            objectToPersist.importStage = 'IN_REVIEW';
          }

          // Set importDocument object
          if (this.importFileSelected)
          {
            if (objectToPersist.importDocument === null || objectToPersist.importDocument === undefined)
            {
              objectToPersist.importDocument = {};
            }
            objectToPersist.importDocument.name = this.importFilename.value;
            objectToPersist.importDocument.mimeType = this.selectedFileMimeType;
            const base64 = this.selectedFile.split('base64,')[1];
            objectToPersist.importDocument.file = base64;
          }

          delete objectToPersist.importFilename;

          // Set default to PATIENT if there is no Channel selected.
          if (objectToPersist.channel === null || objectToPersist.channel === '' || objectToPersist.channel === undefined)
          {
            objectToPersist.pcpCreationLevel = 'PATIENT';
            objectToPersist.contactCreationLevel = 'PATIENT';
          }

          this.save.emit(objectToPersist);
          this.editFormActive = false;
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Process the code CSV file import.
   */
  public handleCsvInput(files: FileList): void
  {
    const csvFile = files.item(0);

    this.importFileSelected = true;
    this.editForm.importFilename = csvFile.name;
    this.importFilename.setValue(csvFile.name);
    this.selectedFileMimeType = csvFile.type;
    this.editForm.markAsDirty();
    this.setFileFromBlob(csvFile);
  }

  /**
   * Check if item from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.status !== 'Online';
  }

  /**
   * Check if item from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public organizationItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Filter the organizations based on user input.
   */
  public filterOrganizations(filterValue: string): void
  {
    this.organizationsFiltered = sortBy(this.organizations
      .filter(organization => organization.name.toLowerCase().includes(filterValue.toLowerCase())), ['name']);
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(patientImport: PatientImportEdit)
  {
    if (patientImport === undefined)
    {
      return;
    }

    this.currentUser = this.authenticationService.currentUser;
    if (this.currentUser)
    {
      this.userService.getUserById(this.currentUser.id, false).subscribe({
        next: (user) =>
        {
          this.currentUserOrganization = user.organization;

          this.translate.get(['DEFAULT-BATCH']).subscribe({
            next: (res) =>
            {
              this.defaultBatchText = res['DEFAULT-BATCH'];

              // Set the current category id used for name async validators
              const previousPatientImportName = this.isNew ? '' : patientImport.name;
              const defaultBatchName = this.defaultBatchText.replace('{{value}}', moment().format('YYYYMMDD_hhmmssSSS'));

              this.isLocked = this.isNew ? false : patientImport.locked;
              this.importFileSelected = false;

              // Set form
              this.editForm = this.fb.group({
                id: [this.isNew ? null : patientImport.id],
                version: [this.isNew ? null : patientImport.version],
                name: [
                  { value: this.isNew ? defaultBatchName : patientImport.name, disabled: patientImport.locked },
                  {
                    validators: [Validators.required],
                    asyncValidators: (): Observable<ValidationErrors | null> =>
                      this.patientImportValidators.validatePatientImportNameUnique(this.editForm !== undefined ?
                        this.editForm.get('name') : null, previousPatientImportName)
                  }
                ],
                description: [{ value: this.isNew ? null : patientImport.description, disabled: patientImport.locked }],
                automaticImport: [{ value: this.isNew ? false : patientImport.automaticImport, disabled: patientImport.locked }],
                automaticDiscardOnError: [{
                  value: this.isNew ?
                    false : patientImport.automaticDiscardOnError, disabled: patientImport.locked
                }],
                sendInvite: [{
                  value: this.isNew ? false : patientImport.sendInvite, disabled: patientImport.locked
                }],
                inviteTemplate: [{
                  value: this.isNew ?
                    this.defaultSendInviteTemplate : patientImport.inviteTemplate, disabled: patientImport.locked
                }],
                inviteSendOn: [{
                  value: this.isNew ? new Date() :
                    patientImport.inviteSendOn !== null ? new Date(patientImport.inviteSendOn) : null, disabled: patientImport.locked
                }],
                inviteValidDays: [{
                  value: this.isNew ?
                    this.defaultValidDays : patientImport.inviteValidDays, disabled: patientImport.locked
                }],
                importFilename: [
                  {
                    value: this.isNew ? null : patientImport.importDocument !== null ? patientImport.importDocument.name : null,
                    disabled: patientImport.locked
                  },
                  {
                    validators: [Validators.required]
                  }
                ],
                channel: [{ value: this.isNew ? null : patientImport.channel, disabled: patientImport.locked }],
                referralChannel: [{ value: this.isNew ? null : patientImport.referralChannel, disabled: patientImport.locked }],
                pcpCreationLevel: [patientImport.pcpCreationLevel !== '' ?
                  patientImport.pcpCreationLevel : 'PATIENT', Validators.required],
                contactCreationLevel: [patientImport.contactCreationLevel !== '' ?
                  patientImport.contactCreationLevel : 'PATIENT', Validators.required],
                organization: [{
                  value: this.isNew ?
                    this.currentUserOrganization : patientImport.organization, disabled: patientImport.locked
                },
                {
                  validators: [Validators.required]
                }],
                subjectIdentifiers: [{ value: this.isNew ? null : this.getSubjectIdentifiers(patientImport.subjectIdentifiers), disabled: patientImport.locked }]
              });

              this.editForm.get('sendInvite').valueChanges.subscribe({
                next: (sendInvite: boolean) =>
                {
                  this.setControlStatesInvite(sendInvite);
                }
              });

              this.editForm.get('channel').valueChanges.subscribe({
                next: (channel: boolean) =>
                {
                  this.setControlStatesChannel(channel);
                }
              });

              // Set the channel
              this.channelService.getCacheMinimalChannels().subscribe({
                next: (channelData) =>
                {
                  this.allChannels = channelData.filter(x => x.referral === false);
                  this.allChannels.forEach(d =>
                  {
                    d.programName = d.program.name;
                  });

                  this.allChannelsReferrals = channelData.filter(x => x.referral === true);
                  this.allChannelsReferrals.forEach(d =>
                  {
                    d.programName = d.program.name;
                  });

                  // Check if Channel is still online, if not we need to fetch this program separatly.
                  if (this.channel.value !== null)
                  {
                    this.addChannelToListIfMissing(this.channel.value.id, this.allChannels, false);
                  }

                  if (this.referralChannel.value !== null)
                  {
                    this.addChannelToListIfMissing(this.referralChannel.value.id, this.allChannelsReferrals, true);
                  }

                  this.setGroupedChannels();
                  this.setGroupedChannelsReferrals();
                  this.setControlStatesInvite(this.sendInvite.value);
                  this.setControlStatesChannel(this.channel.value);
                  this.patientImportEdit = patientImport;
                  this.editFormActive = true;
                }
              });
            }
          });
        }
      });
    }
  }

  private getSubjectIdentifiers(value: any): Array<any>
  {
    return value !== '' && value !== null ? value.split(',') : [];
  }

  private addChannelToListIfMissing(channelId: number, listChannels: any[], isReferral: boolean): void
  {
    const channelFound = listChannels.filter(x => x.id === channelId);
    if (channelFound.length === 0)
    {
      this.channelService.getMinimalChannelById(channelId).subscribe({
        next: (lookupChannel) =>
        {
          if (lookupChannel.length === 1)
          {
            lookupChannel.forEach(d =>
            {
              d.programName = d.program.name;
            });
            // Add Channel to allChannels array to show but will not be able to select (will be disabled)
            listChannels.push(lookupChannel[0]);
            if (isReferral)
            {
              this.setGroupedChannelsReferrals();
            }
            else
            {
              this.setGroupedChannels();
            }
          }
          else
          {
            // Skip the load since we can't retrieve the channelId.
            this.messageHandlerService.displayWarning('CHANNEL-NOT-FOUND');
          }
        }
      });
    }
  }

  private setControlStatesChannel(channel: any): void
  {
    if (channel && channel !== undefined && channel !== null && channel !== '' && !this.isLocked)
    {
      this.editForm.get('pcpCreationLevel').enable();
      this.editForm.get('contactCreationLevel').enable();
    }
    else
    {
      this.editForm.get('pcpCreationLevel').disable();
      this.editForm.get('contactCreationLevel').disable();
      if (!this.isLocked)
      {
        this.pcpCreationLevel.setValue('PATIENT');
        this.contactCreationLevel.setValue('PATIENT');
      }
    }
    this.editForm.get('pcpCreationLevel').updateValueAndValidity();
    this.editForm.get('contactCreationLevel').updateValueAndValidity();
  }

  private setControlStatesInvite(sendInvite: boolean): void
  {
    const inviteValidator = [];
    if (sendInvite)
    {
      inviteValidator.push(Validators.required);
      if (this.inviteSendOn.value === null)
      {
        this.inviteSendOn.setValue(new Date());
      }
      if (this.inviteValidDays.value === null)
      {
        this.inviteValidDays.setValue(this.defaultValidDays);
      }
      if (this.inviteTemplate.value === null)
      {
        this.inviteTemplate.setValue(this.defaultSendInviteTemplate);
      }
    }
    this.editForm.get('inviteTemplate').setValidators(inviteValidator);
    this.editForm.get('inviteTemplate').updateValueAndValidity();
    this.editForm.get('inviteSendOn').setValidators(inviteValidator);
    this.editForm.get('inviteSendOn').updateValueAndValidity();
    this.editForm.get('inviteValidDays').setValidators(inviteValidator);
    this.editForm.get('inviteValidDays').updateValueAndValidity();
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      this.selectedFile = reader.result;
    }, false);
    reader.readAsDataURL(file);
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels = groupBy(this.allChannels, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }

  private setGroupedChannelsReferrals(): void
  {
    // Build the grouped Channels
    const groupedChannelsReferrals = groupBy(this.allChannelsReferrals, [{ field: 'programName' }]);
    this.groupedChannelsReferrals.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannelsReferrals = sortBy(groupedChannelsReferrals, ['value']);
  }
}
